import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/** A row whose document price deviates from the customer's price, with the reviewer's decision. */
export interface AiOrderParserPriceDeviation {
  rowNumber: number;
  productId: string;
  documentPrice: number;
  customerPrice: number;
  /** Which price the reviewer chose to submit. */
  chosen: 'document' | 'customer' | 'manual';
  currencyCode: string;
}

/** One order row as it appears on the confirmation receipt, priced with the chosen unit price. */
export interface AiOrderParserReceiptRow {
  rowNumber: number;
  productId: string;
  /** Product master-data name; empty when the product could not be resolved. */
  productName: string;
  quantity: number;
  /** The unit price that will be submitted (document price when chosen); null when unresolvable. */
  unitPrice: number | null;
  /** quantity × unitPrice; null when the unit price is unresolvable. */
  lineTotal: number | null;
  currencyCode: string;
  /** True when the document's price was chosen over the customer's price. */
  usedDocumentPrice: boolean;
  /** The customer's list price, shown in the doc-marker tooltip; null when unresolvable. */
  customerPrice: number | null;
}

/** One order-level value as it will reach the ERP. */
export interface AiOrderParserSubmitField {
  /** Translation key for the label. */
  labelKey: string;
  /** The value that will be submitted; empty when the form holds none. */
  value: string;
  /** Shown in place of an empty value: the translation key for what happens without it. */
  fallbackKey?: string;
}

/**
 * Snapshot of what is about to be submitted to the ERP, shown for a final confirmation.
 * Terms and delivery fields come from the delivery customer's ERP master data and are
 * hidden when the ERP does not provide them.
 */
export interface AiOrderParserSubmitSummary {
  deliveryCustomerId: string;
  deliveryCustomerName: string;
  billingCustomerId: string;
  customerOrderNumber: string;
  /** The reviewed sender email; empty when none or when the installation does not map it. */
  senderEmail: string;
  /** Requested delivery date as yyyy-MM-dd; empty when none was given. */
  deliveryDate: string;
  rowCount: number;
  totalQuantity: number;
  /** Rows currently failing validation; they are still part of the submitted order. */
  invalidRowCount: number;
  /** Order total over the chosen unit prices; null when prices could not be resolved for all rows. */
  totalValue: number | null;
  currencyCode: string;
  /** Every order-level field that is submitted, in reading order, with its own empty-value fallback. */
  orderFields: AiOrderParserSubmitField[];
  deliveryAddress: string[];
  /** True when the address shown is the customer's registered one because the form carries none. */
  deliveryAddressFromCustomer: boolean;
  /** The flow the order is placed in (from the config or the settings); empty when none. */
  flowName: string;
  /** The flow's order params that go on the order head, unless the document carries its own value. */
  flowParams: AiOrderParserSubmitField[];
  /** Rows deviating from the customer's prices, each with the price the reviewer chose. */
  priceDeviations: AiOrderParserPriceDeviation[];
  /** Every order row as a receipt line (REDESIGN.md §5). */
  rows: AiOrderParserReceiptRow[];
}

@Component({
  selector: 'lib-pdf-ai-parser-confirm-submit-modal',
  standalone: false,
  templateUrl: './pdf-ai-parser-confirm-submit-modal.component.html',
  styleUrls: ['./pdf-ai-parser-confirm-submit-modal.component.scss']
})
export class PdfAiParserConfirmSubmitModalComponent {
  @Input() summary!: AiOrderParserSubmitSummary;

  /** Busy state for the footer; the default flow closes on confirm, so this stays false unless driven. */
  isSubmitting = false;

  /** Receipt pagination — long orders show 20 rows at a time. */
  readonly pageSize = 20;
  page = 0;

  constructor(public activeModal: NgbActiveModal) {}

  get pagedRows(): AiOrderParserReceiptRow[] {
    const start = this.page * this.pageSize;
    return this.summary.rows.slice(start, start + this.pageSize);
  }

  get pageCount(): number {
    return Math.ceil(this.summary.rows.length / this.pageSize);
  }

  get pageFrom(): number {
    return this.page * this.pageSize + 1;
  }

  get pageTo(): number {
    return Math.min((this.page + 1) * this.pageSize, this.summary.rows.length);
  }

  prevPage(): void {
    this.page = Math.max(0, this.page - 1);
  }

  nextPage(): void {
    this.page = Math.min(this.pageCount - 1, this.page + 1);
  }

  /** Compact "Stockholm, Sverige" line for the collapsed address disclosure. */
  get addressSummaryLine(): string {
    return this.summary.deliveryAddress.slice(-2).join(', ');
  }

  /** True when at least one empty field falls back to an ERP default, so the footnote earns its place. */
  get hasErpDefaults(): boolean {
    return this.summary.orderFields.some(field => !field.value && !!field.fallbackKey);
  }

  /** Deviating rows resolved to the document's price. */
  get documentPriceCount(): number {
    return this.summary.priceDeviations.filter(deviation => deviation.chosen === 'document').length;
  }

  /** Deviating rows resolved to the customer's price (picked as-is or typed manually). */
  get customerPriceCount(): number {
    return this.summary.priceDeviations.filter(
      deviation => deviation.chosen === 'customer' || deviation.chosen === 'manual'
    ).length;
  }

  confirm(): void {
    this.activeModal.close(true);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
